// 'use server';
import { getBareunBaseUrl } from '#/data/constants';
import {
  AnalyzeSyntaxResponseSchema,
  LanguageService,
  TokenizeResponseSchema
} from '#/protobuf/bareun/language_service_pb';
import {
  DictSearchAnchor,
  DictSearchService,
  SearchDictResponseSchema
} from '#/protobuf/bareun/dict_search_service_pb';
import { toProtoJsonResult } from '#/lib/proto-json';
import { createClient } from '@connectrpc/connect';
import { createConnectTransport } from '@connectrpc/connect-web';

const USER_AGENT = 'bareun-web';

// 요청 deadline. 종전 호출부가 쓰던 값과 동일하게 유지한다.
const DEADLINE_MS = 120000;

export function makeHeaders(apiKey: string, agent = USER_AGENT) {
  const h = new Headers();
  h.append('api-key', apiKey);
  h.append('Content-Type', 'application/json');
  // 로그 식별자
  h.append('User-Agent', agent); // 표준 UA
  h.append('userAgent', agent); // 표준 UA
  h.append('X-Client-App', agent); // (옵션) 커스텀 식별 헤더
  h.append(
    'x-bareun-web',
    'k7mX2pQnR9vLsW4dYjBhT6cZaUeNgFoC3iKxVwMbP8qAyEtHrDuJsOlIf5nGz1'
  ); // (옵션) 커스텀 식별 헤더
  return h;
}

/**
 * bareun 서버(connect-go)용 Connect 클라이언트.
 *
 * 종전에는 fetch 로 `/bareun.LanguageService/AnalyzeSyntax` 에 JSON 을 POST 했다.
 * 그게 사실 Connect 단항(unary) 프로토콜을 손으로 구현한 것이었어서, proto 코드
 * 생성을 protoc-gen-es(v2) 로 옮기면서 정식 Connect 클라이언트로 교체했다.
 * 요청 경로·헤더·JSON 본문 형태는 종전과 같고, 필드 이름을 손으로 맞추지 않아도
 * 되는 것(그리고 응답이 타입을 갖는 것)이 달라진 점이다.
 */
function createLanguageClient(apiKey: string) {
  const transport = createConnectTransport({
    baseUrl: getBareunBaseUrl(),
    // 종전 fetch 호출이 보내던 식별 헤더를 그대로 유지한다.
    interceptors: [
      (next) => (req) => {
        makeHeaders(apiKey).forEach((value, key) => {
          // Content-Type 은 Connect 가 프로토콜에 맞춰 직접 설정한다.
          if (key.toLowerCase() !== 'content-type') {
            req.header.set(key, value);
          }
        });
        return next(req);
      }
    ]
  });

  return createClient(LanguageService, transport);
}

/** Connect 오류를 종전 handleResponse 와 같은 `{ ok: false, error }` 형태로 변환한다. */
function toErrorResult(error: unknown) {
  const message =
    error && typeof error === 'object' && 'message' in error
      ? (error as { message?: string }).message
      : undefined;
  return {
    ok: false as const,
    error: message || '요청 처리 중 알 수 없는 오류가 발생했습니다.'
  };
}

export async function getAnalyze(
  apiKey: string,
  auto_split_sentence: boolean,
  auto_spacing: boolean,
  auto_jointing: boolean,
  custom_domain: string,
  encoding_type: number,
  document: { content: string; language: string },
  // WSD(동형이의어 의미 구분). 기본값 false — 켜지 않으면 응답·비용이 종전과 같다.
  with_sense = false
) {
  try {
    const client = createLanguageClient(apiKey);
    const res = await client.analyzeSyntax(
      {
        document,
        encodingType: encoding_type,
        autoSplitSentence: auto_split_sentence,
        autoSpacing: auto_spacing,
        autoJointing: auto_jointing,
        customDomain: custom_domain,
        withSense: with_sense
      },
      { timeoutMs: DEADLINE_MS }
    );
    return toProtoJsonResult(AnalyzeSyntaxResponseSchema, res);
  } catch (error) {
    return toErrorResult(error);
  }
}

/**
 * 후처리 없는 순수 형태소 분석(AnalyzeSyntaxRaw).
 *
 * seg_model + tag_model 추론 결과를 그대로 준다 — 복합명사·복합동사 분해, 자동
 * 띄어쓰기·붙여쓰기를 적용하지 않고 사용자 사전도 반영하지 않는다. 그래서 요청에
 * auto_spacing·auto_jointing·custom_domain 이 아예 없다(있어도 서버가 무시하는 게
 * 아니라 필드 자체가 없는 별도 메시지다).
 */
export async function getAnalyzeRaw(
  apiKey: string,
  auto_split_sentence: boolean,
  encoding_type: number,
  document: { content: string; language: string },
  with_sense = false
) {
  try {
    const client = createLanguageClient(apiKey);
    const res = await client.analyzeSyntaxRaw(
      {
        document,
        encodingType: encoding_type,
        autoSplitSentence: auto_split_sentence,
        withSense: with_sense
      },
      { timeoutMs: DEADLINE_MS }
    );
    // 응답 메시지는 AnalyzeSyntax 와 같다(proto 주석).
    return toProtoJsonResult(AnalyzeSyntaxResponseSchema, res);
  } catch (error) {
    return toErrorResult(error);
  }
}

export async function getTokenize(
  apiKey: string,
  auto_split_sentence: boolean,
  auto_spacing: boolean,
  encoding_type: number,
  document: { content: string; language: string }
) {
  try {
    const client = createLanguageClient(apiKey);
    const res = await client.tokenize(
      {
        document,
        encodingType: encoding_type,
        autoSplitSentence: auto_split_sentence,
        autoSpacing: auto_spacing
      },
      { timeoutMs: DEADLINE_MS }
    );
    return toProtoJsonResult(TokenizeResponseSchema, res);
  } catch (error) {
    return toErrorResult(error);
  }
}

/**
 * 우리말샘 음소(자소) 검색 옵션. 필드 의미는 dict_search_service.proto 와 1:1 이다.
 */
export interface SearchDictOptions {
  /** 패턴 위치. 기본 WORD(표제어 전체 일치). */
  anchor?: DictSearchAnchor;
  /** 품사 필터(비우면 전체, OR). '동사'·'명사' 또는 별칭('용언'·'체언' 등). */
  pos?: string[];
  /** true 면 비표준어·방언·북한어 제외. */
  stdOnly?: boolean;
  /** 최대 표제어 수(기본 100, 상한 1000). */
  limit?: number;
  /** 건너뛸 표제어 수(페이지 넘기기). */
  offset?: number;
  /** true 면 뜻풀이·용례 포함. */
  withDefinition?: boolean;
  /** true 면 개수만(넓은 패턴 규모 확인). */
  countOnly?: boolean;
}

/**
 * 우리말샘 음소 검색(DictSearchService/SearchDict, baikal-nlp#1899).
 *
 * 자소 슬롯 패턴("{ㅅ//ㄴ}다", "*{//ㅎ}다", suffix "아지")을 서버가 NFD 정규식으로
 * 바꿔 우리말샘 사전 전체를 훑는다. 손으로 자소 정규식을 만들지 않는 것이 규약이다 —
 * 키보드의 호환용 자모와 NFD 조합용 자모가 다른 문자라 조용히 0건이 나온다.
 *
 * rev(교정 포함) 빌드 서버에만 있는 서비스라, 기본 빌드 서버에 붙으면 오류가 난다
 * (운영 api.bareun.ai 는 rev). 패턴 문법 오류는 서버가 한국어 메시지의
 * InvalidArgument 로 돌려주며 그대로 토스트에 쓴다.
 */
export async function searchDict(
  apiKey: string,
  pattern: string,
  opts: SearchDictOptions = {}
) {
  try {
    const transport = createConnectTransport({
      baseUrl: getBareunBaseUrl(),
      interceptors: [
        (next) => (req) => {
          makeHeaders(apiKey).forEach((value, key) => {
            if (key.toLowerCase() !== 'content-type') {
              req.header.set(key, value);
            }
          });
          return next(req);
        }
      ]
    });
    const client = createClient(DictSearchService, transport);
    const res = await client.searchDict(
      {
        pattern,
        anchor: opts.anchor ?? DictSearchAnchor.WORD,
        pos: opts.pos ?? [],
        stdOnly: opts.stdOnly ?? false,
        limit: opts.limit ?? 0,
        offset: opts.offset ?? 0,
        withDefinition: opts.withDefinition ?? false,
        countOnly: opts.countOnly ?? false
      },
      { timeoutMs: DEADLINE_MS }
    );
    return toProtoJsonResult(SearchDictResponseSchema, res);
  } catch (error) {
    return toErrorResult(error);
  }
}
